import React, { useState, useEffect } from 'react';
import { Search, CreditCard, Zap, ShieldCheck, User, Clock, History, ArrowUpRight, Lock } from 'lucide-react';
import { agenciesAPI } from '../utils/api';

const AgencyRecharge = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchUsers = async (query = '') => {
    try {
      const response = await agenciesAPI.getUsersList(query);
      setUsers(response.data.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await agenciesAPI.getRechargeHistory();
      setHistory(response.data.data);
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchHistory();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    fetchUsers(e.target.value);
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await agenciesAPI.rechargeUser({ userId: selectedUser.id, amount });
      setShowRechargeModal(false);
      setAmount('');
      fetchUsers(search);
      fetchHistory();
    } catch (err) {
      console.error(err.response?.data?.message || 'Recharge Failed');
      alert(err.response?.data?.message || 'Recharge Failed');
    } finally {
      setLoading(false);
    }
  };

  const openRechargeModal = (user) => {
    setSelectedUser(user);
    setAmount('');
    setShowRechargeModal(true);
  };

  return (
    <div className="flex flex-col xl:flex-row gap-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Left Section: User Search */}
      <div className="flex-1 space-y-10">
        <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div>
              <h3 className="text-3xl font-black text-white tracking-tight">Search Users</h3>
              <p className="text-slate-500 text-sm mt-1 font-bold uppercase tracking-widest">Instant Recharge Portal</p>
            </div>
            <div className="relative w-full md:w-[400px]">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
              <input 
                type="text" 
                placeholder="Search by ID or Name..."
                className="w-full bg-white/5 border border-white/10 rounded-[24px] pl-16 pr-8 py-5 text-white outline-none focus:border-pink-500/50 transition-all font-bold"
                value={search}
                onChange={handleSearch}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {users.map((user) => (
              <div key={user.id} className="bg-white/5 border border-white/5 rounded-[32px] p-6 hover:bg-white/10 transition-all group">
                 <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="absolute -inset-1 bg-gradient-to-tr from-pink-600 to-indigo-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity"></div>
                      <img 
                        src={user.avatar_url ? (user.avatar_url.startsWith('http') ? user.avatar_url : `http://localhost:5001${user.avatar_url}`) : 'https://i.pravatar.cc/150'} 
                        className="relative w-16 h-16 rounded-2xl object-cover"
                        alt=""
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-black text-white truncate">{user.first_name} {user.last_name}</h4>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">ID: {user.user_id}</p>
                    </div>
                    <button 
                      onClick={() => openRechargeModal(user)}
                      className="p-4 bg-pink-600/10 text-pink-500 rounded-2xl hover:bg-pink-600 hover:text-white transition-all shadow-lg"
                    >
                      <Zap size={20} />
                    </button>
                 </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Section: Recent Activity */}
      <div className="w-full xl:w-96 space-y-10">
        <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-10 h-full">
           <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-black text-white">Recent Activity</h3>
              <History size={20} className="text-slate-600" />
           </div>

           <div className="space-y-8">
              {history.slice(0, 8).map((item) => (
                <div key={item.id} className="flex items-center gap-4 group">
                   <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-slate-500 group-hover:bg-pink-500/10 group-hover:text-pink-500 transition-all">
                      <Clock size={20} />
                   </div>
                   <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-bold text-white truncate">{item.user_name}</p>
                        <ArrowUpRight size={14} className="text-emerald-500" />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                          {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs font-black text-white">+{parseFloat(item.amount).toLocaleString()}</p>
                      </div>
                   </div>
                </div>
              ))}
           </div>
        </div>
      </div>

      {/* Recharge Modal */}
      {showRechargeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
           <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-10 max-w-md w-full shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-pink-600 to-indigo-600"></div>
              
              <div className="text-center mb-8">
                 <div className="w-16 h-16 bg-pink-500/10 rounded-2xl flex items-center justify-center text-pink-500 mx-auto mb-4">
                    <Zap size={32} />
                 </div>
                 <h3 className="text-2xl font-black text-white tracking-tight">Instant Recharge</h3>
                 <p className="text-slate-500 text-sm mt-2 font-bold uppercase tracking-widest">To: <span className="text-white">{selectedUser?.first_name}</span></p>
              </div>

              <form onSubmit={handleRecharge} className="space-y-6">
                <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Amount (XP)</label>
                    <input 
                      type="number"
                      required
                      min={10}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:border-pink-500 outline-none transition-all text-center text-3xl font-black"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                </div>

                <div className="grid grid-cols-3 gap-3">
                    {[100, 500, 1000].map(amt => (
                      <button 
                        key={amt} 
                        type="button" 
                        onClick={() => setAmount(amt.toString())}
                        className="py-3 bg-white/5 rounded-xl text-sm font-bold hover:bg-white/10 transition-all border border-white/5"
                      >
                          +{amt}
                      </button>
                    ))}
                </div>

                <div className="flex gap-4">
                    <button 
                      type="button" 
                      onClick={() => setShowRechargeModal(false)}
                      className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all"
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit"
                      disabled={loading || !amount}
                      className="flex-[2] py-4 bg-gradient-to-r from-pink-600 to-indigo-600 text-white rounded-2xl font-bold hover:shadow-[0_0_20px_rgba(219,39,119,0.4)] transition-all disabled:opacity-50"
                    >
                      {loading ? 'Processing...' : 'Confirm Recharge'}
                    </button>
                </div>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AgencyRecharge;
